import styled from 'styled-components';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/router';
// components
import BothHeader from 'components/layout/headers/Both';
import FillButton from 'cds/buttons/Fill';
import OutlineButton from 'cds/buttons/Outline';
import IconButton from 'cds/buttons/Icon';
import Icon from 'cds/icons';
import MainLayout from 'cds/layout/Main';
import VerticalScrollLayout from 'cds/layout/VerticalScroll';
import SpinLoading from 'components/atoms/common/SpinLoading';
import Layout from 'components/layout';
// slices
import { getNoticeList } from 'slices/service.thunk';
// hooks
import useInfiniteScroll from 'hooks/common/useInfiniteScroll';
import useCommonBridge from 'hooks/bridge/useCommonBridge';
import { useSelector, useDispatch } from 'hooks/common/useStore';
import useMetaData from 'hooks/common/useMetaData';
// utils
import { stringTimeFormatter } from 'lib/utils/format/time';
import { copyToClipboard } from 'lib/utils/clipboard';
// styles
import { palette, mediaQuery, mediaValue } from 'cds/styles';
import * as typography from 'cds/typography';

/** 공지사항 페이지 */
const NoticePage = () => {
  const dispatch = useDispatch();
  const noticeList = useSelector(state => state.service.noticeList);

  const [isLoading, setIsLoading] = useState(true);
  const enableMoreData = useRef(true);
  const page = useRef(1);
  const target = useRef(null);

  const { MetaTitle } = useMetaData();

  useInfiniteScroll({
    target,
    onIntersect: ([entry]) => {
      if (entry.isIntersecting) {
        onLoadData();
      }
    },
  });

  const onLoadData = useCallback(async () => {
    if (!enableMoreData.current) {
      return;
    }

    setIsLoading(true);
    enableMoreData.current = false;

    const res = await dispatch(getNoticeList(page.current));

    setIsLoading(false);
    if (res.list?.length) {
      page.current += 1;
      enableMoreData.current = true;
    }
  }, [dispatch]);

  useEffect(() => onLoadData(), [onLoadData]);

  /** 공지사항 리스트 만들어주는 함수 */
  const NoticeItem = ({ notice }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isCopy, setIsCopy] = useState(false);

    const onClick = e => {
      const content = e.target.nextSibling;

      if (isOpen) {
        content.style.display = '';
      } else {
        content.style.display = 'block';
      }

      setIsOpen(v => !v);
    };

    const onCopy = () => {
      const isTest = process.env.NEXT_PUBLIC_TEST === 'true';
      const host = isTest
        ? process.env.NEXT_PUBLIC_TEST_HOST
        : process.env.NEXT_PUBLIC_DEFAULT_HOST;
      copyToClipboard(`${host}/new/notice/${notice.id}`);
      setIsCopy(true);
    };

    const Content = () => ({ __html: notice.content });

    return (
      <>
        <DropdownItem color={palette.grey010} size="Large" onClick={onClick} selected={isOpen}>
          <span>{notice.subject}</span>
          <Icon name="ic_arrow_down_r" width={24} height={24} fill={palette.grey0} />
        </DropdownItem>
        <DropdownContent selected={isOpen}>
          <p>{stringTimeFormatter(notice.created_at)}</p>
          <div dangerouslySetInnerHTML={Content()} />
          <ShareButton size="Small" color={palette.BTN.BG} isCopy={isCopy} onClick={onCopy}>
            {!isCopy && (
              <>
                <Icon name="ic_copy" width={18} height={18} fill={palette.grey50} />
                링크 복사하기
              </>
            )}
            {isCopy && <>링크 복사 완료!</>}
          </ShareButton>
        </DropdownContent>
      </>
    );
  };

  const NoticeList = noticeList.map((v, idx) => <NoticeItem key={idx} notice={v} />);

  return (
    <>
      <MetaTitle content="공지사항" />

      <Wrapper>
        <VerticalScrollLayout>
          <Title>공지사항</Title>
          {NoticeList}
          <div ref={target} />
        </VerticalScrollLayout>

        {isLoading && (
          <LoadingLayout>
            <SpinLoading />
          </LoadingLayout>
        )}
      </Wrapper>
    </>
  );
};

const Header = () => {
  const router = useRouter();

  const { onCloseWebView } = useCommonBridge();

  const onBack = () => {
    if (!onCloseWebView()) {
      router.back();
    }
  };

  return (
    <BothHeader
      message={`공지사항`}
      leftSide={
        <IconButton width={24} height={54} onClick={onBack}>
          <Icon
            name="ic_back_m"
            width={24}
            height={24}
            stroke={palette.grey75}
            fill={palette.grey75}
          />
        </IconButton>
      }
    />
  );
};

NoticePage.getLayout = function getLayout(page) {
  return <Layout mobileHeader={<Header />}>{page}</Layout>;
};

const Wrapper = styled(MainLayout)`
  width: ${mediaValue.large}px;
  padding: 24px 16px;

  ${mediaQuery.large} {
    padding: 48px 72px 64px;
  }
`;

const LoadingLayout = styled.div`
  margin-top: -64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 0 24px 0;
`;

const Title = styled.h1`
  border-bottom: 1px solid ${palette.BTN.BG};
  padding-bottom: 8px;
  margin-bottom: 16px;
  color: ${palette.grey75};
  ${typography.h1_medium};
  font-size: 24px;

  ${mediaQuery.large} {
    font-size: 32px;
  }
`;

const DropdownItem = styled(FillButton)`
  width: 100%;
  padding: 8px 16px;
  justify-content: space-between;
  background-color: ${({ selected }) => (selected ? palette.grey0 : palette.BTN.BG)};
  color: ${({ selected }) => (selected ? palette.grey75 : palette.grey50)};
  font-size: 12px;
  cursor: pointer;

  & strong {
    padding-right: 12px;
  }

  & > span {
    text-align: left;
  }

  & svg {
    margin-left: 16px;
    fill: ${({ selected }) => (selected ? palette.white : palette.grey50)};
    transform: ${({ selected }) => (selected ? `rotate(180deg)` : `rotate(0)`)};
    transform-origin: center;
  }

  ${mediaQuery.large} {
    padding: 12px 20px;
    font-size: 14px;
  }

  & ~ & {
    margin-top: 8px;
  }
`;

const DropdownContent = styled.div`
  display: none;
  padding: 24px 16px;
  ${typography.body3_regular};
  transition: 0.3s ease;
  overflow: hidden;
  & > p {
    color: ${palette.grey040};
    margin-bottom: 16px;
  }

  * {
    color: ${palette.grey50} !important;
  }

  a {
    &:hover {
      text-decoration: underline !important;
    }
  }

  p {
    color: ${palette.grey50};
  }
`;

const ShareButton = styled(OutlineButton)`
  flex-grow: 0;
  width: 140px;
  padding: 0 8px;
  margin: 16px 0 0 auto;

  background-color: ${({ isCopy }) => (isCopy ? palette.Primary.Active : palette.BTN.BG)};
  color: ${({ isCopy }) => (isCopy ? palette.white : palette.grey50)};

  svg {
    margin-right: 8px;
  }
`;

export default NoticePage;
